import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, Minus, ArrowRight, MapPin } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useSEO } from '../hooks/useSEO';

import { API_BASE as API } from '../utils/api';

const PLANS = [
  {
    key: 'free', name: 'Free', monthlyPrice: 0, annualPrice: 0, noAccount: true,
    description: 'Get started, explore projects',
    features: ['25 application views/mo', 'AI Intelligence Reports', 'Estimated project values', 'Postcode district shown'],
    notIncluded: ['Full street addresses', 'Email alerts', 'Physical letter sending'],
  },
  {
    key: 'starter', name: 'Starter', monthlyPrice: 79, annualPrice: 790,
    description: 'For individual tradespeople',
    features: ['200 application views/mo', 'Full street addresses revealed', '5 letters/mo included', 'Weekly email alerts', 'Planning documents & EPC data', 'Email support'],
    notIncluded: ['Opportunity scoring', 'AI letter copy', 'Automated outreach'],
  },
  {
    key: 'intelligence', name: 'Intelligence', monthlyPrice: 149, annualPrice: 1490, featured: true,
    description: 'AI-powered insights for pros',
    features: ['1,000 application views/mo', 'Everything in Starter', '25 letters/mo included', 'Opportunity scoring (1-10)', 'Trade recommendations', 'Approval likelihood analysis', 'Instant email + SMS alerts', 'AI-generated letter copy', 'Priority support'],
    notIncluded: ['Automated letter sending', 'Team accounts'],
  },
  {
    key: 'pro', name: 'Pro', monthlyPrice: 199, annualPrice: 1990,
    description: 'Full platform for growing teams',
    features: ['2,500 application views/mo', 'Everything in Intelligence', '50 letters/mo included', 'Automated letter sending', 'Neighbour radar (nearby leads)', 'WhatsApp alerts', 'API access', 'Team accounts (up to 5)', 'Custom postcode alerts', 'Dedicated account manager', 'Export data to CSV'],
    notIncluded: [],
  },
];

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);
  const { user } = useAuth();
  const [loading, setLoading] = useState('');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromReveal = searchParams.get('from') === 'reveal';

  useSEO({
    title: 'Pricing — Plans from £79/month | PlanningSearch',
    description: 'PlanningSearch pricing: Free to browse, Starter £79/mo (unlimited views), Intelligence £149/mo (AI reports & value estimates), Pro £199/mo (automated outreach & team accounts). Annual plans include 2 months free.',
    canonical: 'https://planningsearch.com/pricing',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      'name': 'PlanningSearch Pricing',
      'description': 'Subscription plans for PlanningSearch UK planning intelligence platform.',
      'mainEntity': {
        '@type': 'Product',
        'name': 'PlanningSearch',
        'description': 'UK planning application intelligence platform',
        'offers': [
          { '@type': 'Offer', 'name': 'Free', 'price': '0', 'priceCurrency': 'GBP' },
          { '@type': 'Offer', 'name': 'Starter', 'price': '79', 'priceCurrency': 'GBP', 'billingDuration': 'P1M' },
          { '@type': 'Offer', 'name': 'Intelligence', 'price': '149', 'priceCurrency': 'GBP', 'billingDuration': 'P1M' },
          { '@type': 'Offer', 'name': 'Pro', 'price': '199', 'priceCurrency': 'GBP', 'billingDuration': 'P1M' }
        ]
      }
    },
  });

  const handleSubscribe = async (planKey) => {
    if (planKey === 'free') { navigate('/map'); return; }
    if (!user) { navigate('/signup'); return; }
    const interval = annual ? 'annual' : 'monthly';
    const fullKey = `${planKey}_${interval}`;
    setLoading(fullKey);
    try {
      const { data } = await axios.post(`${API}/api/checkout/create-session`, { plan: fullKey, origin_url: window.location.origin }, { withCredentials: true });
      if (data.url) window.location.href = data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      alert('Failed to create checkout session.');
    } finally {
      setLoading('');
    }
  };

  return (
    <div className="min-h-[calc(100vh-56px)] bg-white overflow-x-hidden" data-testid="pricing-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.1em] text-zinc-400 mb-2">Pricing</p>
          <h1 className="font-['Manrope'] text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-zinc-900 mb-3">
            Plans for every stage of your business
          </h1>
          <p className="text-zinc-500 text-sm max-w-md mx-auto mb-8 px-2">Start free. Upgrade when you're ready. Cancel anytime.</p>
          <div className="flex items-center justify-center gap-3" data-testid="billing-toggle">
            <span className={`text-sm font-medium ${!annual ? 'text-zinc-900' : 'text-zinc-400'}`}>Monthly</span>
            <Switch checked={annual} onCheckedChange={setAnnual} className="data-[state=checked]:bg-emerald-600" data-testid="annual-toggle" />
            <span className={`text-sm font-medium ${annual ? 'text-zinc-900' : 'text-zinc-400'}`}>
              Annual <span className="text-emerald-600 text-xs font-semibold ml-1">2 months free</span>
            </span>
          </div>
        </div>

        {fromReveal && (
          <div className="mb-8 bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4 flex items-center gap-3" data-testid="reveal-banner">
            <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 text-emerald-700" />
            </div>
            <p className="text-sm text-emerald-800"><span className="font-semibold">Unlock full addresses and instant alerts</span> — upgrade from £79/mo.</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="pricing-cards">
          {PLANS.map((plan) => {
            const price = plan.key === 'free' ? 0 : annual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice;
            const isCurrentPlan = user && user.plan === plan.key;
            const planKey = plan.key === 'free' ? 'free' : `${plan.key}_${annual ? 'annual' : 'monthly'}`;

            return (
              <div key={plan.key}
                className={`rounded-lg p-5 flex flex-col ${
                  plan.featured ? 'bg-white border-2 border-emerald-600 shadow-lg shadow-emerald-50 relative' :
                  plan.key === 'free' ? 'bg-zinc-50 border border-zinc-200' :
                  'bg-white border border-zinc-200 shadow-sm'
                }`}
                data-testid={`plan-card-${plan.key}`}>
                {plan.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-emerald-700 text-white text-[10px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap">
                    Most Popular
                  </div>
                )}
                <div>
                  <h3 className="font-['Manrope'] text-base font-bold text-zinc-900">{plan.name}</h3>
                  <p className="text-xs text-zinc-500 mt-0.5">{plan.description}</p>
                </div>
                <div className="mt-4 mb-1">
                  {plan.key === 'free' ? (
                    <div className="flex items-baseline gap-1">
                      <span className="font-['Manrope'] text-3xl font-extrabold text-zinc-900">£0</span>
                      <span className="text-xs text-zinc-400">forever</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-baseline gap-1">
                        <span className="font-['Manrope'] text-3xl font-extrabold text-zinc-900">£{price}</span>
                        <span className="text-xs text-zinc-400">/mo</span>
                      </div>
                      {annual && <p className="text-[10px] text-zinc-400 mt-0.5">£{plan.annualPrice} billed annually</p>}
                    </>
                  )}
                </div>

                <button onClick={() => handleSubscribe(plan.key)}
                  disabled={isCurrentPlan || (loading === planKey && planKey !== 'free')}
                  className={`w-full py-2 text-sm font-semibold rounded-md transition-colors mt-4 mb-5 disabled:opacity-50 ${
                    plan.featured ? 'bg-emerald-700 text-white hover:bg-emerald-800' :
                    plan.key === 'free' ? 'bg-zinc-200 text-zinc-700 hover:bg-zinc-300' :
                    'border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
                  }`}
                  data-testid={`subscribe-${plan.key}-button`}>
                  {isCurrentPlan ? 'Current Plan' : plan.key === 'free' ? 'Browse Free' : loading === planKey ? 'Loading...' : 'Get started'}
                </button>

                <ul className="space-y-2 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px]">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-600">{f}</span>
                    </li>
                  ))}
                  {plan.notIncluded.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px]">
                      <Minus className="w-3.5 h-3.5 text-zinc-300 mt-0.5 flex-shrink-0" />
                      <span className="text-zinc-400">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mt-20" data-testid="pricing-faq">
          <h2 className="font-['Manrope'] text-xl font-bold text-zinc-900 mb-6 text-center">Common Questions</h2>
          <div className="space-y-3">
            {[
              { q: 'Can I cancel anytime?', a: 'Yes, cancel from your dashboard. You retain access until the end of your billing period.' },
              { q: 'Is searching really free?', a: 'Yes — free users get 25 application views per month, including full details and AI reports. Upgrade from £79/mo for 200 views and full street addresses.' },
              { q: 'What are AI Intelligence Reports?', a: 'Our AI analyses each planning application to provide opportunity scores (1-10), estimated project values, recommended trades, approval likelihood, and a plain-English summary of the opportunity. Available to all users.' },
              { q: 'How does the annual billing work?', a: 'Pay for 10 months and get 12 — that\'s 2 months free. You can switch between monthly and annual at any time.' },
              { q: 'What are physical letters?', a: 'Paid subscribers get a monthly allowance of printed letters sent directly to applicants — 5/mo on Starter, 25/mo on Intelligence, 75/mo on Pro. Extra letters are available at £0.80 each. Pro users can automate sending based on custom rules.' },
              { q: 'Where does the data come from?', a: 'Planning data is sourced from public council records. We update regularly but always recommend verifying details with the relevant local planning authority for critical decisions.' },
            ].map(({ q, a }) => (
              <div key={q} className="bg-zinc-50 rounded-md p-5">
                <h4 className="text-sm font-semibold text-zinc-900 mb-1">{q}</h4>
                <p className="text-sm text-zinc-500">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
